import * as React from 'react';
import { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useTranslation } from 'react-i18next';
import { Alert, Snackbar } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { usePostHog } from 'posthog-js/react';
import { EventService, MessageRecipientType, WaitingListService } from '../../../api';
import { RichTextEditor } from '../../../common/ui/RichTextEditor';
import { ColorButton } from '../../../common/ui/ConfimationDialog';

type SendEmailDialogProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  eventId?: string;
  recipientType?: MessageRecipientType;
  waitingListItemId?: string;
  emailAddress?: string;
};
export default function SendEmailDialog({
  isOpen,
  setIsOpen,
  eventId,
  recipientType = MessageRecipientType.GUESTS,
  waitingListItemId,
  emailAddress,
}: SendEmailDialogProps) {
  const { t } = useTranslation();
  const posthog = usePostHog();

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [successOpen, setSuccessOpen] = useState(false);
  const handleClose = () => {
    setIsOpen(false);
  };

  const save = async () => {
    setLoading(true);
    if (waitingListItemId && emailAddress) {
      await WaitingListService.sendWaitingListMessage({
        waiting_list_item_id: waitingListItemId,
        email_address: emailAddress,
        message,
      });
    } else {
      if (!eventId) {
        setLoading(false);
        return;
      }
      await EventService.sendEmails({ event_id: eventId, message, recipient_type: recipientType });
    }
    posthog.capture('Email send to participants', { eventId, recipientType });
    setSuccessOpen(true);
    setLoading(false);
    handleClose();
  };

  return (
    <div>
      <Dialog open={isOpen} onClose={handleClose} fullWidth>
        <DialogTitle>
          {emailAddress ? t('sendEmailDialog.titleSingleEmail', { email: emailAddress }) : t('sendEmailDialog.title')}
        </DialogTitle>
        <DialogContent>
          <RichTextEditor value={message} onChange={setMessage} readOnly={loading} minLines={6} />
        </DialogContent>
        <DialogActions>
          <ColorButton onClick={handleClose}>{t('cancel')}</ColorButton>
          <LoadingButton
            loading={loading}
            variant="contained"
            onClick={save}
            autoFocus
            disabled={!message || message === '<p><br></p>'}>
            {t('send')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={successOpen}
        onClose={() => setSuccessOpen(false)}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setSuccessOpen(false)} severity="success">
          {t('sendEmailDialog.success')}
        </Alert>
      </Snackbar>
    </div>
  );
}
